<template>
  <aside class="flex h-full">
    <primary-sidebar
      :logo-source="globalConfig.logoThumbnail"
      :installation-name="globalConfig.installationName"
      :is-a-custom-branded-instance="isACustomBrandedInstance"
      :account-id="accountId"
      :menu-items="primaryMenuItems"
      :active-menu-item="activePrimaryMenu.key"
      :navigation-locked="billingLocked"
      @toggle-accounts="toggleAccountModal"
      @key-shortcut-modal="toggleKeyShortcutModal"
      @open-notification-panel="openNotificationPanel"
      @open-announcements-modal="openAnnouncementsModal"
    />
    <secondary-sidebar
      v-if="showSecondarySidebar && !isOnActivitiesPage"
      :class="sidebarClassName"
      :account-id="accountId"
      :inboxes="inboxes"
      :labels="labels"
      :account-forms="accountForms"
      :teams="teams"
      :custom-views="customViews"
      :menu-config="activeSecondaryMenu"
      :current-user="currentUser"
      :is-on-chatwoot-cloud="isOnChatwootCloud"
      :is-billing-deployment="isBillingDeployment"
      :is-many-talks-deployment="isManyTalksDeployment"
      :navigation-locked="billingLocked"
      @add-label="showAddLabelPopup"
      @toggle-accounts="toggleAccountModal"
    />
  </aside>
</template>

<script>
import { mapGetters } from 'vuex';
import { getSidebarItems } from './config/default-sidebar';
import {
  getPrimaryMenuKeyForRoute,
  resolvePrimaryMenuItem,
} from './config/primaryMenuHelper';

import PrimarySidebar from './sidebarComponents/Primary.vue';
import SecondarySidebar from './sidebarComponents/Secondary.vue';
import keyboardEventListenerMixins from 'shared/mixins/keyboardEventListenerMixins';
import router, { routesWithPermissions } from '../../routes';
import {
  getUserPermissions,
  hasPermissions,
} from '../../helper/permissionsHelper';
import { useBillingAccess } from 'dashboard/composables/useBillingAccess';

export default {
  components: {
    PrimarySidebar,
    SecondarySidebar,
  },
  mixins: [keyboardEventListenerMixins],
  props: {
    showSecondarySidebar: {
      type: Boolean,
      default: true,
    },
    sidebarClassName: {
      type: String,
      default: '',
    },
  },
  setup() {
    const { billingLocked, canManageBilling } = useBillingAccess();
    return { billingLocked, canManageBilling };
  },
  data() {
    return {
      showOptionsMenu: false,
    };
  },

  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      currentRole: 'getCurrentRole',
      currentUser: 'getCurrentUser',
      globalConfig: 'globalConfig/get',
      inboxes: 'inboxes/getInboxes',
      isACustomBrandedInstance: 'globalConfig/isACustomBrandedInstance',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      isOnChatwootCloud: 'globalConfig/isOnChatwootCloud',
      isBillingDeployment: 'globalConfig/isBillingDeployment',
      isManyTalksDeployment: 'globalConfig/isManyTalksDeployment',
      labels: 'labels/getLabelsOnSidebar',
      accountForms: 'accountForms/getAccountForms',
      teams: 'teams/getMyTeams',
    }),
    activeCustomView() {
      if (this.activePrimaryMenu.key === 'contacts') {
        return 'contact';
      }
      if (this.activePrimaryMenu.key === 'conversations') {
        return 'conversation';
      }
      return '';
    },
    customViews() {
      return this.$store.getters['customViews/getCustomViewsByFilterType'](
        this.activeCustomView
      );
    },
    isConversationOrContactActive() {
      return (
        this.activePrimaryMenu.key === 'contacts' ||
        this.activePrimaryMenu.key === 'conversations'
      );
    },
    sideMenuConfig() {
      return getSidebarItems(this.accountId);
    },
    primaryMenuItems() {
      const userPermissions = getUserPermissions(
        this.currentUser,
        this.accountId
      );
      const menuItems = this.sideMenuConfig.primaryMenu;
      return menuItems
        .filter(menuItem => {
          if (menuItem.key === 'contacts' && this.hideContactsForAgents) {
            return false;
          }

          const resolvedItem = resolvePrimaryMenuItem(menuItem, {
            accountId: this.accountId,
            currentRole: this.currentRole,
          });

          const isAvailableForTheUser = hasPermissions(
            routesWithPermissions[resolvedItem.toStateName],
            userPermissions
          );

          if (!isAvailableForTheUser) {
            return false;
          }
          if (
            menuItem.alwaysVisibleOnChatwootInstances &&
            !this.isACustomBrandedInstance
          ) {
            return true;
          }
          if (menuItem.featureFlag) {
            return this.isFeatureEnabledonAccount(
              this.accountId,
              menuItem.featureFlag
            );
          }
          return true;
        })
        .map(menuItem => {
          const resolvedItem = resolvePrimaryMenuItem(menuItem, {
            accountId: this.accountId,
            currentRole: this.currentRole,
          });
          const settingsAllowed =
            resolvedItem.key === 'settings' && this.canManageBilling;
          return {
            ...resolvedItem,
            disabled: this.billingLocked && !settingsAllowed,
          };
        });
    },
    activeSecondaryMenu() {
      const { secondaryMenu } = this.sideMenuConfig;
      const { name: currentRoute } = this.$route;

      const activeSecondaryMenu =
        secondaryMenu.find(menuItem =>
          menuItem.routes.includes(currentRoute)
        ) || {};
      return activeSecondaryMenu;
    },
    activePrimaryMenu() {
      const fromSecondary = this.activeSecondaryMenu.parentNav;
      if (fromSecondary) {
        return (
          this.primaryMenuItems.find(menuItem => menuItem.key === fromSecondary) ||
          {}
        );
      }

      const routeKey = getPrimaryMenuKeyForRoute(this.$route);
      if (routeKey) {
        return (
          this.primaryMenuItems.find(menuItem => menuItem.key === routeKey) || {}
        );
      }

      return {};
    },
    isOnActivitiesPage() {
      return this.$route.name === 'activities_view';
    },
    hideContactsForAgents() {
      return (
        this.isFeatureEnabledonAccount(
          this.accountId,
          'hide_contacts_for_agent'
        ) && this.currentRole !== 'administrator'
      );
    },
  },

  watch: {
    activeCustomView() {
      this.fetchCustomViews();
    },
  },
  mounted() {
    if (this.billingLocked) return;
    this.$store.dispatch('labels/get');
    this.$store.dispatch('labelGroups/get');
    this.$store.dispatch('accountForms/get');
    this.$store.dispatch('inboxes/get');
    this.$store.dispatch('notifications/unReadCount');
    this.$store.dispatch('teams/get');
    this.$store.dispatch('attributes/get');
    this.fetchCustomViews();
  },

  methods: {
    fetchCustomViews() {
      if (this.isConversationOrContactActive) {
        this.$store.dispatch('customViews/get', this.activeCustomView);
      }
    },
    toggleKeyShortcutModal() {
      this.$emit('open-key-shortcut-modal');
    },
    closeKeyShortcutModal() {
      this.$emit('close-key-shortcut-modal');
    },
    getKeyboardEvents() {
      return {
        '$mod+Slash': this.toggleKeyShortcutModal,
        '$mod+Escape': this.closeKeyShortcutModal,
        'Alt+KeyC': {
          action: () => this.navigateToRoute('home'),
        },
        'Alt+KeyV': {
          action: () => this.navigateToRoute('contacts_dashboard'),
        },
        'Alt+KeyR': {
          action: () => this.navigateToRoute('account_overview_reports'),
        },
        'Alt+KeyS': {
          action: () => this.navigateToRoute('agent_list'),
        },
      };
    },
    navigateToRoute(routeName) {
      if (this.billingLocked) return;
      if (!this.isCurrentRouteSameAsNavigation(routeName)) {
        router.push({ name: routeName });
      }
    },
    isCurrentRouteSameAsNavigation(routeName) {
      return this.$route.name === routeName;
    },
    toggleSupportChatWindow() {
      window.$chatwoot.toggle();
    },
    toggleAccountModal() {
      this.$emit('toggle-account-modal');
    },
    showAddLabelPopup() {
      this.$emit('show-add-label-popup');
    },
    openNotificationPanel() {
      this.$emit('open-notification-panel');
    },
    openAnnouncementsModal() {
      this.$emit('open-announcements-modal');
    },
  },
};
</script>
